/**
 * Echt · Notify me, Back in Stock provider.
 *
 * The AMP Back in Stock widget binds to a button with the id `BIS_trigger` and
 * records the signup against that button's `data-variant-id`. The theme owns
 * the button, the app owns the popup and the email. Live does exactly this
 * (~/dev/echt/sections/product.liquid:1992).
 *
 * Liquid renders the panel only for a sold-out variant, but Horizon changes
 * variants without a reload and does NOT re-render this block when it does.
 * Measured 8 Sep 2026: pick the one sold-out size, then pick an in-stock one,
 * and "Email me when back in stock" was still sitting under an enabled ADD TO
 * CART. So the panel's visibility, its variant id and its heading are all
 * maintained here.
 *
 * Availability is read from the add-to-cart button rather than a variant map:
 * that button is what Horizon itself updates on every variant change, so it
 * cannot disagree with the buy column, and it needs no second copy of the
 * product's inventory in the page.
 *
 * Every write below is guarded by a comparison, and the observer is suspended
 * across them. Without both, the first version froze the renderer: assigning
 * `textContent` is a childList mutation inside the subtree being observed, so
 * the callback re-entered itself forever.
 */
const PANEL = '.echt-notify';
const TRIGGER = '#BIS_trigger';
const FORM = 'form[action*="/cart/add"]';

function selectedSizeLabel() {
  const checked = document.querySelector('input[name*="Size"]:checked, input[data-axis="size"]:checked');
  return checked instanceof HTMLInputElement ? checked.value.trim() : '';
}

let syncing = false;

function sync() {
  if (syncing) return;
  const panel = document.querySelector(PANEL);
  if (!(panel instanceof HTMLElement)) return;

  const form = document.querySelector(FORM);
  const submit = form?.querySelector('button[type="submit"]');
  const input = form?.querySelector('input[name="id"]');
  // No button to read means no safe answer; leave whatever Liquid rendered.
  if (!(submit instanceof HTMLButtonElement)) return;

  syncing = true;
  try {
    apply(panel, submit.disabled, input);
  } finally {
    // A microtask, not a straight reset: the mutations we just made are
    // delivered to the observer after this turn, and the flag has to still be
    // up when they arrive.
    queueMicrotask(() => {
      syncing = false;
    });
  }
}

/**
 * @param {HTMLElement} panel
 * @param {boolean} soldOut
 * @param {Element | null | undefined} input
 */
function apply(panel, soldOut, input) {
  if (panel.hidden === soldOut) panel.hidden = !soldOut;
  if (!soldOut) return;

  if (input instanceof HTMLInputElement && input.value) {
    const trigger = panel.querySelector(TRIGGER);
    if (trigger instanceof HTMLElement && trigger.dataset.variantId !== input.value) {
      trigger.dataset.variantId = input.value;
    }
  }

  // The heading names the size it is offering to watch, so a stale one is a
  // promise about the wrong garment.
  const heading = panel.querySelector('.echt-notify__heading');
  const label = selectedSizeLabel();
  if (heading instanceof HTMLElement && label) {
    const tail = heading.dataset.tail ?? (heading.dataset.tail = heading.textContent.split('—').pop().trim());
    const next = `${label} — ${tail}`;
    if (heading.textContent !== next) heading.textContent = next;
  }
}

for (const type of ['variant:update', 'shopify:section:load', 'popstate', 'change']) {
  document.addEventListener(type, sync);
}

const observer = new MutationObserver(sync);
const start = () => {
  const scope = document.querySelector('.product-details, .product-information') ?? document.body;
  observer.observe(scope, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled'] });
  sync();
};

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
else start();
